// Field metadata for the cycle low / high tuning UI. Shared between
// the admin page (bulk editing) and the chart-side live-preview panel.
//
// Each field's `baseKey` is built from the library's per-timeframe key
// helper, so callers always land on the canonical new-schema shape
// (`indicators.cycleLow.<tf>.<field>` / `indicators.cycleHigh.<tf>.<field>`).
// Phase 3a uses the factories below to make per-timeframe variants for
// the live tuning panel; AdminPage still consumes the WEEKLY_*_FIELDS
// arrays until 3b factors its sections to use the same machinery.
import { cycleLowSettingKey, cycleHighSettingKey } from '@andywilliams/indicators';

// Field copy is timeframe-neutral by design — the helperText talks about
// "cycle low" rather than "weekly cycle low" so the same descriptions can
// safely be re-used for daily and hourly tuning. Field names that still
// say "weekly" (weeklySmaReclaimBars etc.) are a separate naming concern
// (ticket T6); the algorithm treats those as TF-neutral SMA references.
const LOW_FIELD_DEFS = [
  {
    field: 'clusterRange',
    label: 'Cluster Range (bars)',
    helperText: 'Maximum bar distance between neighbouring swing lows that are grouped into a single cycle-low cluster.',
    inputProps: { min: 1, step: 1 },
  },
  {
    field: 'dssOversoldLevel',
    label: 'DSS Oversold Level',
    helperText: 'DSS oversold threshold used when scoring cycle lows (default 20).',
  },
  {
    field: 'dssOversoldLookback',
    label: 'DSS Oversold Lookback (bars)',
    helperText: 'Number of bars before the pivot to scan for a DSS oversold touch.',
  },
  {
    field: 'dssConfirmBars',
    label: 'DSS Confirm Bars',
    helperText: 'Standard DSS cross-up confirmation window (in bars) after the pivot.',
  },
  {
    field: 'dssExtendedConfirmBars',
    label: 'DSS Extended Confirm Bars',
    helperText: 'Extended DSS cross-up window used when DSS is choppy in the 15–30 band around the pivot.',
  },
  {
    field: 'weeklySmaReclaimBars',
    label: '10-SMA Reclaim Bars',
    helperText: 'Maximum bars after the pivot for price to reclaim the 10-SMA.',
  },
  {
    field: 'weeklySmaHoldBars',
    label: '10-SMA Hold Bars',
    helperText: 'Number of consecutive bars required to hold above the 10-SMA after reclaim.',
  },
  {
    field: 'acceptThreshold',
    label: 'Acceptance Threshold',
    helperText: 'Score threshold required for a cycle low to be accepted (batch and live modes).',
  },
  {
    field: 'minCycleBars',
    label: 'Min Cycle Bars',
    helperText: 'Minimum bars between consecutive confirmed cycle lows. Mirrors per-asset cycle ranges (BTC weekly 24, SPX weekly 22, etc.). Optional — leave blank for no constraint.',
    inputProps: { min: 1, step: 1 },
  },
];

const HIGH_FIELD_DEFS = [
  {
    field: 'clusterRange',
    label: 'Cluster Range (bars)',
    helperText: 'Maximum bar distance between neighbouring swing highs that are grouped into a single cycle-high cluster.',
    inputProps: { min: 1, step: 1 },
  },
  {
    field: 'dssOverboughtLevel',
    label: 'DSS Overbought Level',
    helperText: 'DSS overbought threshold used when scoring cycle highs (default 80).',
  },
  {
    field: 'dssOverboughtLookback',
    label: 'DSS Overbought Lookback (bars)',
    helperText: 'Number of bars before the pivot to scan for a DSS overbought touch.',
  },
  {
    field: 'dssConfirmBars',
    label: 'DSS Confirm Bars',
    helperText: 'Standard DSS cross-down confirmation window (in bars) after the pivot.',
  },
  {
    field: 'dssExtendedConfirmBars',
    label: 'DSS Extended Confirm Bars',
    helperText: 'Extended DSS cross-down window used when DSS is choppy in the 60–80 band around the pivot.',
  },
  {
    field: 'weeklySmaRejectionBars',
    label: '10-SMA Rejection Bars',
    helperText: 'Maximum bars after the pivot for price to reject the 10-SMA from below.',
  },
  {
    field: 'weeklySmaHoldBelowBars',
    label: '10-SMA Hold Below Bars',
    helperText: 'Number of consecutive bars required to hold below the 10-SMA after rejection.',
  },
  {
    field: 'acceptThreshold',
    label: 'Acceptance Threshold',
    helperText: 'Score threshold required for a cycle high to be accepted (batch and live modes).',
  },
  {
    field: 'minCycleBars',
    label: 'Min Cycle Bars',
    helperText: 'Minimum bars between consecutive confirmed cycle highs. Mirrors per-asset cycle ranges (BTC weekly 24, SPX weekly 22, etc.). Optional — leave blank for no constraint.',
    inputProps: { min: 1, step: 1 },
  },
];

const buildField = (def, baseKey) => ({
  field: def.field,
  baseKey,
  label: def.label,
  helperText: def.helperText,
  type: 'number',
  inputProps: def.inputProps || { min: 0, step: 1 },
});

// Per-timeframe field arrays. The shape (field, baseKey, label, helperText,
// type, inputProps) is identical to what the static WEEKLY_* arrays returned
// pre-Phase-3a; the only thing that changes per timeframe is `baseKey`.
export const getCycleLowFields = (timeframe) =>
  LOW_FIELD_DEFS.map((def) => buildField(def, cycleLowSettingKey(timeframe, def.field)));

export const getCycleHighFields = (timeframe) =>
  HIGH_FIELD_DEFS.map((def) => buildField(def, cycleHighSettingKey(timeframe, def.field)));

// Back-compat for callers that still hardcode weekly. AdminPage uses these
// until 3b factors its bulk-edit sections to use the per-TF factory.
export const WEEKLY_CYCLE_LOW_FIELDS = getCycleLowFields('weekly');
export const WEEKLY_CYCLE_HIGH_FIELDS = getCycleHighFields('weekly');

// Per-symbol override key builder. Slash-normalisation matches the admin
// writer and the scheduled-jobs reader. Prefer `cycleLow/HighSettingKey(tf,
// field, symbol)` from @andywilliams/indicators at new call sites where you
// already have the field name handy.
export const buildSettingKey = (baseKey, symbol) => {
  if (!symbol) {return baseKey;}
  const normalised = String(symbol).trim().replace(/\//g, '-');
  if (!normalised) {return baseKey;}
  return `${baseKey}.${normalised}`;
};

// Legacy (pre-Phase-2b) base keys for the weekly schema. Exported so writers
// can delete them alongside a new-schema write/clear on weekly to prevent
// the library's dual-read from resurrecting a pre-migration override.
// Daily and hourly never had a legacy schema, so no equivalent exports.
export const LEGACY_WEEKLY_CYCLE_LOW_BASE_KEYS = LOW_FIELD_DEFS.reduce(
  (acc, def) => ({ ...acc, [def.field]: `indicators.weeklyCycleLow.${def.field}` }),
  {},
);

export const LEGACY_WEEKLY_CYCLE_HIGH_BASE_KEYS = HIGH_FIELD_DEFS.reduce(
  (acc, def) => ({ ...acc, [def.field]: `indicators.weeklyCycleHigh.${def.field}` }),
  {},
);
